/* @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CanvasTexture, DoubleSide, LinearFilter, Mesh, MeshStandardMaterial, Object3D, PlaneGeometry, } from 'three';
import { ImageObject } from './ImageObject.js';
/**
 * Audio object that can play audio files and display a waveform visualization.
 */
export class AudioObject extends Object3D {
    constructor(iconUrl) {
        super();
        this._src = null;
        this._iconObject = null;
        this._audioElement = null;
        this._iconUrl = null;
        this._iconUrl = iconUrl || null;
        // Create canvas for waveform visualization
        this._canvas = document.createElement('canvas');
        this._canvas.width = 512;
        this._canvas.height = 256;
        this._canvasCtx = this._canvas.getContext('2d');
        // Draw initial placeholder
        this._drawPlaceholder();
        this._texture = new CanvasTexture(this._canvas);
        this._texture.minFilter = LinearFilter;
        this._texture.needsUpdate = true;
        const geometry = new PlaneGeometry();
        const material = new MeshStandardMaterial();
        material.side = DoubleSide;
        material.map = this._texture;
        material.emissiveMap = this._texture;
        material.emissiveIntensity = 1.0;
        material.roughness = 1.0;
        material.metalness = 0.0;
        this._mesh = new Mesh(geometry, material);
        this._mesh.name = 'AudioMesh';
        this.add(this._mesh);
        this._mesh.visible = false;
        // Set aspect ratio for the mesh (2:1)
        this._mesh.scale.set(1, 0.5, 1);
    }
    async _loadIcon(url) {
        try {
            const iconObject = new ImageObject();
            await iconObject.load(url);
            this._iconObject = iconObject;
            this.add(iconObject);
            // Hide waveform mesh when icon is loaded
            this._mesh.visible = false;
        }
        catch (error) {
            console.warn('Failed to load audio icon:', error);
            // Continue without icon
        }
    }
    get src() {
        return this._src;
    }
    set src(src) {
        if (src) {
            this.load(src).catch(console.error);
        }
    }
    get audioElement() {
        return this._audioElement;
    }
    _drawPlaceholder() {
        const ctx = this._canvasCtx;
        const width = this._canvas.width;
        const height = this._canvas.height;
        // Background
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, width, height);
        // Draw audio icon
        ctx.fillStyle = '#666';
        ctx.font = '48px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🎵', width / 2, height / 2);
        // Border
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, width - 2, height - 2);
    }
    _loadAudio(src) {
        return new Promise((resolve, reject) => {
            const audio = document.createElement('audio');
            audio.crossOrigin = 'anonymous';
            audio.preload = 'auto';
            audio.loop = true;
            const onLoadedData = () => {
                cleanup();
                resolve(audio);
            };
            const onError = (error) => {
                cleanup();
                reject(new Error(`Error loading audio "${src}": ${error}`));
            };
            const cleanup = () => {
                audio.removeEventListener('loadeddata', onLoadedData);
                audio.removeEventListener('error', onError);
            };
            audio.addEventListener('loadeddata', onLoadedData);
            audio.addEventListener('error', onError);
            audio.src = src;
            audio.load();
        });
    }
    async load(src) {
        this._src = src;
        this._mesh.visible = false;
        // Clean up old audio
        if (this._audioElement) {
            this._audioElement.pause();
            this._audioElement.src = '';
            this._audioElement.load();
        }
        // Always try to load icon - use default if not provided
        const defaultIconUrl = this._iconUrl || './audio-icon.png';
        const iconPromise = this._loadIcon(defaultIconUrl).catch(() => {
            // If default icon fails, try to load from shared-assets as fallback
            if (!this._iconUrl) {
                return this._loadIcon('../shared-assets/audio-icon.png').catch(() => {
                    // If both fail, continue without icon (but don't show waveform)
                    console.warn('Audio icon not available');
                    return Promise.resolve();
                });
            }
            return Promise.resolve();
        });
        this._audioElement = await this._loadAudio(src);
        // Wait for icon to load
        await iconPromise;
        // Ensure waveform mesh is never visible
        this._mesh.visible = false;
        return this;
    }
    play() {
        if (this._audioElement) {
            return this._audioElement.play();
        }
        return Promise.resolve();
    }
    pause() {
        if (this._audioElement) {
            this._audioElement.pause();
        }
    }
    get paused() {
        var _a, _b;
        return (_b = (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.paused) !== null && _b !== void 0 ? _b : true;
    }
    get currentTime() {
        var _a, _b;
        return (_b = (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.currentTime) !== null && _b !== void 0 ? _b : 0;
    }
    set currentTime(value) {
        if (this._audioElement) {
            this._audioElement.currentTime = value;
        }
    }
    get duration() {
        var _a, _b;
        return (_b = (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.duration) !== null && _b !== void 0 ? _b : 0;
    }
    get loop() {
        var _a, _b;
        return (_b = (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.loop) !== null && _b !== void 0 ? _b : true;
    }
    set loop(value) {
        if (this._audioElement) {
            this._audioElement.loop = value;
        }
    }
    get volume() {
        var _a, _b;
        return (_b = (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.volume) !== null && _b !== void 0 ? _b : 1;
    }
    set volume(value) {
        if (this._audioElement) {
            this._audioElement.volume = value;
        }
    }
    update() {
        // Waveform visualization removed - no update needed
    }
    copy(source, recursive = true) {
        if (recursive) {
            this.remove(this._mesh);
        }
        super.copy(source, recursive);
        if (recursive) {
            const _meshIndex = source.children.indexOf(source._mesh);
            if (_meshIndex !== -1) {
                this._mesh = this.children[_meshIndex];
            }
        }
        if (source.src) {
            this.src = source.src;
        }
        return this;
    }
    dispose() {
        if (this._audioElement) {
            this._audioElement.pause();
            this._audioElement.src = '';
            this._audioElement.load();
            this._audioElement = null;
        }
        const material = this._mesh.material;
        if (material.map) {
            material.map.dispose();
        }
        if (material.emissiveMap && material.emissiveMap !== material.map) {
            material.emissiveMap.dispose();
        }
        material.dispose();
        this._mesh.geometry.dispose();
        this.remove(this._mesh);
        this._texture.dispose();
        // Dispose icon object if present
        if (this._iconObject) {
            this._iconObject.dispose();
            this.remove(this._iconObject);
            this._iconObject = null;
        }
    }
    /**
     * Returns true if the audio needs continuous rendering (for visualization)
     */
    needsContinuousRendering() {
        // No continuous rendering needed since waveform is removed
        return false;
    }
}
//# sourceMappingURL=AudioObject.js.map